import React, { useEffect, useState, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { motion } from 'motion/react';
import { X, QrCode, RefreshCw, User, Loader2, MapPin, ShieldCheck } from 'lucide-react';
import { translations, Language } from '../translations';
import { db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { UserProfile } from '../types';
import { CultivationMap } from './CultivationMap';

interface ScannerScreenProps {
  onBack: () => void;
  onNavigateToFarmer: (farmerId: string) => void;
  language: Language;
  setToast: (toast: { message: string, type: 'success' | 'error' | 'info' }) => void;
}

export const ScannerScreen: React.FC<ScannerScreenProps> = ({ onBack, onNavigateToFarmer, language, setToast }) => {
  const t = translations[language];
  const [scanResult, setScanResult] = useState<string | null>(null);
  const [scannedFarmer, setScannedFarmer] = useState<UserProfile | null>(null);
  const [loadingFarmer, setLoadingFarmer] = useState(false);
  const [cameraStatus, setCameraStatus] = useState<'initializing' | 'active' | 'failed'>('initializing');
  const [scannerKey, setScannerKey] = useState(0);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const isStartingRef = useRef<boolean>(false);

  const getFarmerIdFromUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.searchParams.get('farmerId');
    } catch (e) {
      return null;
    }
  };

  const farmerId = scanResult ? (getFarmerIdFromUrl(scanResult) || scanResult) : null;

  useEffect(() => {
    if (farmerId && scanResult) {
      const fetchFarmer = async () => {
        setLoadingFarmer(true);
        try {
          // 1. Try by UID first
          const docRef = doc(db, 'users', farmerId);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            const data = docSnap.data();
            setScannedFarmer({ ...data, uid: farmerId } as UserProfile);
            // Auto-navigate to full profile
            setTimeout(() => onNavigateToFarmer(farmerId), 1500);
          } else {
            // 2. Try by farmerId field
            const q = query(collection(db, 'users'), where('farmerId', '==', farmerId));
            const querySnap = await getDocs(q);
            if (!querySnap.empty) {
              const snap = querySnap.docs[0];
              const uid = snap.id;
              setScannedFarmer({ ...snap.data(), uid } as UserProfile);
              // Auto-navigate to full profile
              setTimeout(() => onNavigateToFarmer(uid), 1500);
            }
          }
        } catch (error) {
          console.error("Error fetching scanned farmer:", error);
          setToast({ message: "Error fetching farmer profile", type: 'error' });
        } finally {
          setLoadingFarmer(false);
        }
      };
      fetchFarmer();
    }
  }, [farmerId, scanResult, setToast]);

  useEffect(() => {
    const html5QrCode = new Html5Qrcode("reader");
    scannerRef.current = html5QrCode;
    let isMounted = true;
    
    const config = { 
      fps: 15, 
      qrbox: { width: 300, height: 300 },
      aspectRatio: 1.0,
    };

    const startScanner = async () => {
      if (!isMounted) return;
      isStartingRef.current = true;
      try {
        setCameraStatus('initializing');
        
        await html5QrCode.start(
          { facingMode: "environment" },
          config,
          (decodedText) => {
            if (!isMounted) return;
            setScanResult(decodedText);
            // Non-blocking stop
            html5QrCode.stop().catch(() => {});
          },
          () => {}
        );
        
        if (isMounted) {
          setCameraStatus('active');
        } else {
          // If we unmounted while starting, stop it now
          html5QrCode.stop().catch(() => {});
        }
      } catch (err) {
        if (isMounted) {
          console.error("Unable to start scanner:", err);
          setCameraStatus('failed');
        }
      } finally {
        isStartingRef.current = false;
      }
    };

    startScanner();

    return () => {
      isMounted = false;
      const cleanup = async () => {
        if (html5QrCode.isScanning) {
          try {
            await html5QrCode.stop();
          } catch (err) {
            // Silently ignore stop errors on cleanup
          }
        }
        try {
          html5QrCode.clear();
        } catch (e) {
          // Ignore clear errors
        }
      };
      cleanup();
      scannerRef.current = null;
    };
  }, [scannerKey]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const html5QrCode = new Html5Qrcode("reader-hidden");
    try {
      setLoadingFarmer(true);
      const decodedText = await html5QrCode.scanFile(file, true);
      setScanResult(decodedText);
    } catch (err) {
      setToast({ message: "No QR code found in image", type: 'error' });
    } finally {
      setLoadingFarmer(false);
      try { html5QrCode.clear(); } catch(e) {}
    }
  };

  const handleReset = () => {
    setScanResult(null);
    setScannedFarmer(null);
    setScannerKey(prev => prev + 1);
  };

  return (
    <div className="fixed inset-0 bg-stone-900 z-[60] flex flex-col font-sans">
      {/* Hidden element for file scanning */}
      <div id="reader-hidden" className="hidden"></div>

      {/* Header Bar */}
      <div className="p-6 flex items-center justify-between text-white relative z-20">
        <button 
          onClick={onBack}
          className="p-2 hover:bg-white/10 rounded-full transition-colors"
        >
          <X size={24} />
        </button>
        <div className="flex items-center gap-4">
          <button className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-full hover:bg-white/20 transition-colors">
            <RefreshCw size={20} className={cameraStatus === 'initializing' ? 'animate-spin' : ''} />
          </button>
          <div className="p-2">
            <QrCode size={24} />
          </div>
          <button className="p-2">
            <div className="w-1 h-1 bg-white rounded-full mb-1" />
            <div className="w-1 h-1 bg-white rounded-full mb-1" />
            <div className="w-1 h-1 bg-white rounded-full" />
          </button>
        </div>
      </div>

      <div className="flex-1 relative overflow-hidden flex flex-col items-center justify-center px-6">
        {!scanResult ? (
          <>
            {/* Viewfinder Container */}
            <div className="relative w-full max-w-[280px] aspect-square rounded-[3rem] overflow-hidden shadow-2xl bg-black">
              <div id="reader" className="w-full h-full object-cover"></div>
              
              {/* Immersive Overlay */}
              <div className="absolute inset-0 pointer-events-none">
                {/* Outer Colored Corners (The Frame) */}
                <div className="absolute top-0 left-0 w-24 h-24 border-t-[10px] border-l-[10px] border-[#F06292] rounded-tl-[3.5rem]" />
                <div className="absolute top-0 right-0 w-24 h-24 border-t-[10px] border-r-[10px] border-[#FFB74D] rounded-tr-[3.5rem]" />
                <div className="absolute bottom-0 left-0 w-24 h-24 border-b-[10px] border-l-[10px] border-[#42A5F5] rounded-bl-[3.5rem]" />
                <div className="absolute bottom-0 right-0 w-24 h-24 border-b-[10px] border-r-[10px] border-[#66BB6A] rounded-br-[3.5rem]" />

                {/* Inner White Corners (The Scan Zone - 300px area) */}
                <div className="absolute inset-[5%]">
                  <div className="absolute top-0 left-0 w-10 h-10 border-t-4 border-l-4 border-white rounded-sm" />
                  <div className="absolute top-0 right-0 w-10 h-10 border-t-4 border-r-4 border-white rounded-sm" />
                  <div className="absolute bottom-0 left-0 w-10 h-10 border-b-4 border-l-4 border-white rounded-sm" />
                  <div className="absolute bottom-0 right-0 w-10 h-10 border-b-4 border-r-4 border-white rounded-sm" />
                </div>

                {/* Subtle Scan Line */}
                <motion.div 
                  animate={{ top: ['25%', '75%'] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="absolute left-[25%] right-[25%] h-0.5 bg-emerald-500/40 shadow-[0_0_15px_rgba(16,185,129,0.5)] z-20"
                />
              </div>

              {cameraStatus === 'initializing' && (
                <div className="absolute inset-0 bg-stone-900 flex flex-col items-center justify-center z-10">
                  <Loader2 className="animate-spin text-emerald-500 mb-3" size={32} />
                  <span className="text-white/50 text-xs font-medium tracking-wider uppercase">Loading Camera...</span>
                </div>
              )}

              {cameraStatus === 'failed' && (
                <div className="absolute inset-0 bg-stone-900 flex flex-col items-center justify-center p-8 text-center z-10">
                  <div className="w-16 h-16 bg-rose-500/20 text-rose-500 rounded-full flex items-center justify-center mb-4">
                    <RefreshCw size={28} />
                  </div>
                  <h3 className="text-white font-bold mb-2">Camera Access Failed</h3>
                  <p className="text-white/60 text-xs leading-relaxed mb-6">Please check permissions and refresh.</p>
                  <button 
                    onClick={() => window.location.reload()}
                    className="px-8 py-2.5 bg-rose-500 text-white rounded-xl text-xs font-bold"
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>

            {/* Gallery Upload Button */}
            <div className="mt-8 w-full flex flex-col items-center gap-6">
              <label className="flex items-center gap-2 bg-white px-6 py-3 rounded-full text-stone-800 font-semibold shadow-xl cursor-pointer active:scale-95 transition-all">
                <User size={18} className="text-stone-400" />
                <span className="text-sm">Upload from gallery</span>
                <input 
                  type="file" 
                  accept="image/*" 
                  className="hidden" 
                  onChange={handleFileUpload}
                />
              </label>

              <div className="text-center px-10">
                <p className="text-white/30 text-[11px] leading-relaxed font-medium tracking-wide">
                  Place the QR code within the frame to automatically scan farmer profiles and marketplace products
                </p>
              </div>
            </div>
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-sm bg-white rounded-[2.5rem] p-8 shadow-2xl"
          >
            <div className="space-y-6 text-center">
              {loadingFarmer ? (
                <div className="py-12 flex flex-col items-center gap-4">
                  <Loader2 className="animate-spin text-[#1b7d36]" size={48} />
                  <p className="text-stone-500 font-medium">Fetching details...</p>
                </div>
              ) : scannedFarmer ? (
                <div className="space-y-6">
                  <div className="relative">
                    <div className="w-24 h-24 bg-emerald-100 rounded-[2rem] flex items-center justify-center text-emerald-600 mx-auto border-4 border-white shadow-xl overflow-hidden">
                      {scannedFarmer.photoURL ? (
                        <img src={scannedFarmer.photoURL} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User size={48} />
                      )}
                    </div>
                    {scannedFarmer.isVerified && (
                      <div className="absolute -bottom-1 right-1/2 translate-x-10 bg-[#1b7d36] text-white p-1.5 rounded-full border-2 border-white shadow-md">
                        <ShieldCheck size={16} />
                      </div>
                    )}
                  </div>

                  <div className="space-y-1">
                    <h2 className="text-2xl font-black text-stone-800">{scannedFarmer.displayName}</h2>
                    <p className="text-emerald-600 font-bold text-xs uppercase tracking-widest">{scannedFarmer.farmingMethods || 'Natural Farmer'}</p>
                    {scannedFarmer.location && (
                      <div className="flex items-center justify-center gap-1 text-stone-400 text-xs mt-1">
                        <MapPin size={12} />
                        <span>{scannedFarmer.location}</span>
                      </div>
                    )}
                  </div>

                  {scannedFarmer.crops && scannedFarmer.crops.length > 0 && (
                    <div className="flex flex-wrap justify-center gap-2">
                      {scannedFarmer.crops.slice(0, 3).map((crop) => (
                        <div key={crop.id} className="bg-stone-50 px-3 py-1.5 rounded-xl border border-stone-100 flex flex-col items-center">
                          <span className="text-[10px] font-bold text-stone-800">{crop.name}</span>
                          {crop.area && <span className="text-[8px] text-stone-400 uppercase tracking-tighter">{language === 'te' ? 'ఎకరాలు' : 'Acres'} {crop.area}</span>}
                        </div>
                      ))}
                      {scannedFarmer.crops.length > 3 && (
                        <div className="bg-stone-50 px-3 py-1.5 rounded-xl border border-stone-100 flex items-center justify-center">
                          <span className="text-[10px] font-bold text-stone-400">+{scannedFarmer.crops.length - 3}</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="bg-stone-50 p-4 rounded-2xl border border-stone-100">
                    <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-1">Scanned ID</p>
                    <p className="text-stone-600 font-mono text-xs break-all">{farmerId}</p>
                  </div>

                  {scannedFarmer.cultivationArea && (
                    <div className="rounded-2xl overflow-hidden border border-stone-100 shadow-sm">
                      <CultivationMap 
                        lat={scannedFarmer.cultivationArea.lat}
                        lng={scannedFarmer.cultivationArea.lng}
                        readOnly={true}
                      />
                    </div>
                  )}

                  {((scannedFarmer.certifications && scannedFarmer.certifications.length > 0) || scannedFarmer.certificateUrl) && (
                    <div className="bg-white p-6 rounded-3xl shadow-sm border border-stone-100 space-y-4 text-left">
                      <h4 className="font-bold text-emerald-800 flex items-center gap-2 border-b border-emerald-50 pb-2">
                        <ShieldCheck size={18} />
                        {t.certifications}
                      </h4>
                      
                      {scannedFarmer.certifications && scannedFarmer.certifications.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {scannedFarmer.certifications.map((cert, idx) => (
                            <span key={idx} className="px-3 py-1 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-full border border-emerald-100 uppercase tracking-wider">
                              {cert}
                            </span>
                          ))}
                        </div>
                      )}

                      {scannedFarmer.certificateUrl && (
                        <div className="space-y-2">
                          <p className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">{t.organicCertificates}</p>
                          <div className="aspect-video rounded-xl overflow-hidden border border-stone-100 bg-stone-50">
                            <img 
                              src={scannedFarmer.certificateUrl} 
                              alt="Organic Certificate" 
                              className="w-full h-full object-contain"
                              referrerPolicy="no-referrer"
                            />
                          </div>
                          {scannedFarmer.certificatePdfUrl && (
                            <a 
                              href={scannedFarmer.certificatePdfUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 text-xs font-bold text-[#1b7d36] hover:underline"
                            >
                              View PDF Certificate
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    onClick={() => onNavigateToFarmer(farmerId!)}
                    className="w-full bg-[#1b7d36] text-white py-4 rounded-2xl font-bold shadow-lg shadow-[#1b7d36]/20 hover:bg-[#16652b] transition-all active:scale-95 flex items-center justify-center gap-2"
                  >
                    <Loader2 className="animate-spin" size={18} />
                    Navigating to full profile...
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <QrCode className="text-green-600" size={40} />
                  </div>
                  <div className="space-y-2">
                    <h2 className="text-xl font-bold text-stone-800">Scan Successful!</h2>
                    <p className="text-stone-500 text-sm">QR Code content detected</p>
                  </div>
                  
                  <div className="bg-stone-50 p-4 rounded-2xl break-all border border-stone-100">
                    <p className="text-stone-600 font-mono text-sm">{scanResult}</p>
                  </div>

                  {farmerId && (
                    <button
                      onClick={() => onNavigateToFarmer(farmerId)}
                      className="w-full bg-[#1b7d36] text-white py-4 rounded-2xl font-bold shadow-md hover:bg-[#16652b] transition-colors"
                    >
                      Go to Marketplace
                    </button>
                  )}

                  {!farmerId && scanResult.startsWith('http') && (
                    <a
                      href={scanResult}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full block bg-emerald-600 text-white py-4 rounded-2xl font-bold shadow-md hover:bg-emerald-700 transition-colors"
                    >
                      Open Link
                    </a>
                  )}

                  {!farmerId && !scanResult.startsWith('http') && (
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(scanResult);
                        setToast({ message: "Copied to clipboard", type: 'success' });
                      }}
                      className="w-full bg-stone-800 text-white py-4 rounded-2xl font-bold shadow-md hover:bg-stone-900 transition-colors"
                    >
                      Copy Result
                    </button>
                  )}
                </div>
              )}

              <button
                onClick={handleReset}
                className="w-full flex items-center justify-center gap-2 text-stone-400 font-bold py-2 hover:text-[#1b7d36] transition-colors text-sm"
              >
                <RefreshCw size={18} />
                Scan Another
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};
